import React from "react";
import {
  Segment,
  Dropdown,
  Loader,
  Button,
  Divider,
  Table,
  Grid,
  Input,
  Label,
  Confirm,
  Placeholder,
  Message,
  Icon,
} from "semantic-ui-react";
import { Redirect, NavLink } from "react-router-dom";

import isEmpty from "lodash/isEmpty";
import find from "lodash/find";
import titleCase from "title-case";

import ToDoModal from "./ToDoModal";
import ClubsModal from "./ClubsModal";
import CreatedModal from "./CreatedModal";
import CheckedModal from "./CheckedModal";
import ReviewedModal from "./ReviewedModal";
import ApprovedModal from "./ApprovedModal";
import PaginationTable from "../../components/PaginationTable/PaginationTable";

import "./Home.css";
import constants from "../../constants";

const _ = {
  isEmpty: isEmpty,
  find: find,
};

const changeCase = {
  titleCase: titleCase,
};

// Loading placeholder components
const ModalPlaceholder = () => (
  <Placeholder style={{ minHeight: 60, margin: "10px 0" }}>
    <Placeholder.Header>
      <Placeholder.Line length="short" />
    </Placeholder.Header>
    <Placeholder.Paragraph>
      <Placeholder.Line length="medium" />
    </Placeholder.Paragraph>
  </Placeholder>
);

const TablePlaceholder = () => (
  <Placeholder fluid>
    <Placeholder.Header>
      <Placeholder.Line />
      <Placeholder.Line />
    </Placeholder.Header>
    <Placeholder.Paragraph>
      <Placeholder.Line />
      <Placeholder.Line />
      <Placeholder.Line />
      <Placeholder.Line />
    </Placeholder.Paragraph>
  </Placeholder>
);

const SectionPlaceholder = ({ title }) => {
  const placeholders = [];
  for (let i = 1; i <= 5; i++) {
    placeholders.push(<ModalPlaceholder key={i} />);
  }

  return (
    <div>
      <h3>{title}</h3>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {placeholders}
      </div>
    </div>
  );
};

// Enhanced loading component for admin grades with delayed loading info
const AdminGradesLoadingState = ({
  loadingQuarterSummary,
  quarterSummaryError,
  quarterSummaryRetryCount,
  retryQuarterSummary,
  loadingData,
  hasData
}) => {
  if (quarterSummaryError) {
    return (
      <Message negative>
        <Message.Header>
          <Icon name="warning circle" />
          Admin Grades Loading Error
        </Message.Header>
        <p>{quarterSummaryError}</p>
        <Button
          color="red"
          onClick={retryQuarterSummary}
          loading={loadingQuarterSummary}
          style={{ marginRight: "10px" }}
        >
          <Icon name="refresh" />
          Retry Now
        </Button>
        <Button
          color="orange"
          onClick={() => window.location.reload()}
        >
          <Icon name="sync" />
          Force Refresh Page
        </Button>
      </Message>
    );
  }

  if (loadingQuarterSummary) {
    const retryText = quarterSummaryRetryCount > 0
      ? ` (Retry ${quarterSummaryRetryCount}/3)`
      : '';

    return (
      <Message info>
        <Message.Header>
          <Loader active inline size="small" />
          Loading Admin Grades Data{retryText}
        </Message.Header>
        <p>
          This may take up to 45 seconds due to the large amount of data being processed.
          {quarterSummaryRetryCount > 0 && <br />}
          {quarterSummaryRetryCount > 0 && "Previous attempt failed, retrying automatically..."}
        </p>
        <div style={{ marginTop: "10px" }}>
          <Button
            basic
            color="blue"
            onClick={() => window.location.reload()}
          >
            Cancel & Refresh Page
          </Button>
        </div>
      </Message>
    );
  }

  // Show waiting message during initial load delay
  if (loadingData) {
    return (
      <Message info>
        <Message.Header>
          <Icon name="clock outline" />
          Admin Grades - Scheduled Loading
        </Message.Header>
        <p>
          Grades data will load in 15 seconds to prevent backend overload with concurrent users.
          <br />
          <em>This extended delay ensures system stability and prevents hanging issues.</em>
        </p>
      </Message>
    );
  }

  // Show initial preparing state if data hasn't loaded yet
  if (!hasData) {
    return (
      <Message info>
        <Message.Header>
          <Loader active inline size="small" />
          Preparing Admin Grades
        </Message.Header>
        <p>
          Initializing data load. This section will begin loading shortly.
        </p>
      </Message>
    );
  }

  return null;
};

// Enhanced loading component for admin conducts with delayed loading info
const AdminConductsLoadingState = ({
  loadingQuarterSummaryConducts,
  quarterSummaryConductsError,
  quarterSummaryConductsRetryCount,
  retryQuarterSummaryConducts,
  loadingData,
  hasData
}) => {
  if (quarterSummaryConductsError) {
    return (
      <Message negative>
        <Message.Header>
          <Icon name="warning circle" />
          Admin Conducts Loading Error
        </Message.Header>
        <p>{quarterSummaryConductsError}</p>
        <Button
          color="red"
          onClick={retryQuarterSummaryConducts}
          loading={loadingQuarterSummaryConducts}
          style={{ marginRight: "10px" }}
        >
          <Icon name="refresh" />
          Retry Now
        </Button>
        <Button
          color="orange"
          onClick={() => window.location.reload()}
        >
          <Icon name="sync" />
          Force Refresh Page
        </Button>
      </Message>
    );
  }

  if (loadingQuarterSummaryConducts) {
    const retryText = quarterSummaryConductsRetryCount > 0
      ? ` (Retry ${quarterSummaryConductsRetryCount}/3)`
      : '';

    return (
      <Message info>
        <Message.Header>
          <Loader active inline size="small" />
          Loading Admin Conducts Data{retryText}
        </Message.Header>
        <p>
          This may take up to 45 seconds due to the large amount of data being processed.
          {quarterSummaryConductsRetryCount > 0 && <br />}
          {quarterSummaryConductsRetryCount > 0 && "Previous attempt failed, retrying automatically..."}
        </p>
        <div style={{ marginTop: "10px" }}>
          <Button
            basic
            color="blue"
            onClick={() => window.location.reload()}
          >
            Cancel & Refresh Page
          </Button>
        </div>
      </Message>
    );
  }

  // Show waiting message during initial load delay
  if (loadingData) {
    return (
      <Message info>
        <Message.Header>
          <Icon name="clock outline" />
          Admin Conducts - Scheduled Loading
        </Message.Header>
        <p>
          Conducts data will load in 25 seconds (10 seconds after grades) to prevent backend overload.
          <br />
          <em>This staggered approach ensures system stability with multiple concurrent users.</em>
        </p>
      </Message>
    );
  }

  // Show initial preparing state if data hasn't loaded yet
  if (!hasData) {
    return (
      <Message info>
        <Message.Header>
          <Loader active inline size="small" />
          Preparing Admin Conducts
        </Message.Header>
        <p>
          Initializing data load. This section will begin loading shortly.
        </p>
      </Message>
    );
  }

  return null;
};

// Enhanced loading component for level summary
const LevelSummaryLoadingState = ({
  loadingLevelSummary,
  levelSummaryError,
  levelSummaryRetryCount,
  retryLevelSummary,
  loadingData,
  hasData
}) => {
  if (levelSummaryError) {
    return (
      <Message negative>
        <Message.Header>
          <Icon name="warning circle" />
          Level Summary Loading Error
        </Message.Header>
        <p>{levelSummaryError}</p>
        <Button
          color="red"
          onClick={retryLevelSummary}
          loading={loadingLevelSummary}
          style={{ marginRight: "10px" }}
        >
          <Icon name="refresh" />
          Retry Now
        </Button>
        <Button
          color="orange"
          onClick={() => window.location.reload()}
        >
          <Icon name="sync" />
          Force Refresh Page
        </Button>
      </Message>
    );
  }

  if (loadingLevelSummary) {
    const retryText = levelSummaryRetryCount > 0
      ? ` (Retry ${levelSummaryRetryCount}/3)`
      : '';

    return (
      <Message info>
        <Message.Header>
          <Loader active inline size="small" />
          Loading Level Summary Data{retryText}
        </Message.Header>
        <p>
          This may take up to 45 seconds due to the large amount of data being processed.
          {levelSummaryRetryCount > 0 && <br />}
          {levelSummaryRetryCount > 0 && "Previous attempt failed, retrying automatically..."}
        </p>
        <div style={{ marginTop: "10px" }}>
          <Button
            basic
            color="blue"
            onClick={() => window.location.reload()}
          >
            Cancel & Refresh Page
          </Button>
        </div>
      </Message>
    );
  }

  // Show waiting message during initial load delay
  if (loadingData) {
    return (
      <Message info>
        <Message.Header>
          <Icon name="clock outline" />
          Level Summary - Scheduled Loading
        </Message.Header>
        <p>
          Level summary data will load in 5 seconds to optimize loading sequence.
          <br />
          <em>This staggered approach ensures reliable data loading.</em>
        </p>
      </Message>
    );
  }

  // Show initial preparing state if data hasn't loaded yet
  if (!hasData) {
    return (
      <Message info>
        <Message.Header>
          <Loader active inline size="small" />
          Preparing Level Summary
        </Message.Header>
        <p>
          Initializing data load. This section will begin loading shortly.
        </p>
      </Message>
    );
  }

  return null;
};

const HomePV = (props) => {
  if (props.auth) {
    const {
      activePage,
      itemPerPage,
      searchKey,
      details,
      detailsAdmin,
      quarter,
      loadingAdmin,
      loadingLevel,
      loadingData,
    } = props;

    if (props.userType === "staff" || props.userType === "cashier") {
      return <Redirect to="/applicants" />;
    }

    let optionsSchoolYears;
    if (props.schoolYears) {
      optionsSchoolYears = props.schoolYears.map((schoolYear, index) => {
        return {
          key: schoolYear.schoolYearId,
          value: schoolYear.schoolYearId,
          text: schoolYear.name,
        };
      });
    }
    const optionsQuarters = constants.quarter;

    let body;

    // SUBJECT VIEW
    if (props.activeButton === "subject") {
      const hasSubjectData = !_.isEmpty(props.perSubjectSummary);
      const hasConductData = !_.isEmpty(props.perSubjectSummaryConducts);

      body = (
        <div>
          {/* SUBJECTS Section */}
          {hasSubjectData ? (
            <div>
              <h3>SUBJECTS</h3>
              <ToDoModal
                title="My Subjects"
                quarter={quarter}
                isGrades={true}
                value={props.perSubjectSummary.summary.toDo.count}
                data={props.perSubjectSummary.summary.toDo.data}
                sectionSubjects={props.perSubjectSummary.sectionSubjects}
                handleNotify={props.handleNotify}
                openConfirm={props.openConfirm}
                openConfirmation={props.openConfirmation}
                closeConfirmation={props.closeConfirmation}
              />
              <CreatedModal
                title="My Subjects"
                quarter={quarter}
                isGrades={true}
                value={props.perSubjectSummary.summary.created.count}
                data={props.perSubjectSummary.summary.created.data}
                sectionSubjects={props.perSubjectSummary.sectionSubjects}
              />
              <CheckedModal
                title="My Subjects"
                quarter={quarter}
                isGrades={true}
                value={props.perSubjectSummary.summary.checked.count}
                data={props.perSubjectSummary.summary.checked.data}
                sectionSubjects={props.perSubjectSummary.sectionSubjects}
              />
              <ReviewedModal
                title="My Subjects"
                quarter={quarter}
                isGrades={true}
                value={props.perSubjectSummary.summary.reviewed.count}
                data={props.perSubjectSummary.summary.reviewed.data}
                sectionSubjects={props.perSubjectSummary.sectionSubjects}
              />
              <ApprovedModal
                title="My Subjects"
                quarter={quarter}
                isGrades={true}
                value={props.perSubjectSummary.summary.approved.count}
                data={props.perSubjectSummary.summary.approved.data}
                sectionSubjects={props.perSubjectSummary.sectionSubjects}
              />
            </div>
          ) : (
            <SectionPlaceholder title="SUBJECTS" />
          )}

          {/* CONDUCTS Section */}
          {hasConductData ? (
            <div>
              <h3>CONDUCTS</h3>
              <ToDoModal
                title="My Subjects"
                quarter={quarter}
                isGrades={false}
                value={props.perSubjectSummaryConducts.summary.toDo.count}
                data={props.perSubjectSummaryConducts.summary.toDo.data}
                sectionSubjects={
                  props.perSubjectSummaryConducts.sectionSubjects
                }
                handleNotify={props.handleNotify}
                openConfirm={props.openConfirm}
                openConfirmation={props.openConfirmation}
                closeConfirmation={props.closeConfirmation}
              />
              <CreatedModal
                title="My Subjects"
                quarter={quarter}
                isGrades={false}
                value={props.perSubjectSummaryConducts.summary.created.count}
                data={props.perSubjectSummaryConducts.summary.created.data}
                sectionSubjects={
                  props.perSubjectSummaryConducts.sectionSubjects
                }
              />
              <CheckedModal
                title="My Subjects"
                quarter={quarter}
                isGrades={false}
                value={props.perSubjectSummaryConducts.summary.checked.count}
                data={props.perSubjectSummaryConducts.summary.checked.data}
                sectionSubjects={
                  props.perSubjectSummaryConducts.sectionSubjects
                }
              />
              <ReviewedModal
                title="My Subjects"
                quarter={quarter}
                isGrades={false}
                value={props.perSubjectSummaryConducts.summary.reviewed.count}
                data={props.perSubjectSummaryConducts.summary.reviewed.data}
                sectionSubjects={
                  props.perSubjectSummaryConducts.sectionSubjects
                }
              />
              <ApprovedModal
                title="My Subjects"
                quarter={quarter}
                isGrades={false}
                value={props.perSubjectSummaryConducts.summary.approved.count}
                data={props.perSubjectSummaryConducts.summary.approved.data}
                sectionSubjects={
                  props.perSubjectSummaryConducts.sectionSubjects
                }
              />
            </div>
          ) : (
            <SectionPlaceholder title="CONDUCTS" />
          )}
        </div>
      );
    }

    // SECTION VIEW
    else if (props.activeButton === "section") {
      const hasSectionData = !_.isEmpty(props.perSectionSummary);
      const hasConductData = !_.isEmpty(props.perSectionSummaryConducts);

      const headers = (
        <Table.Row>
          <Table.HeaderCell textAlign="center">Section</Table.HeaderCell>
          <Table.HeaderCell textAlign="center">Details Type</Table.HeaderCell>
          <Table.HeaderCell textAlign="center">Status</Table.HeaderCell>
        </Table.Row>
      );

      let sectionTable = null;
      if (hasSectionData) {
        const tableBody = props.perSectionSummary.sections.map(
          (section, key) => {
            if (_.isEmpty(props.perSectionSummary.sections)) return null;
            const tableRow = section.details.map((detail, i) => {
              if (detail.quarter === props.quarter) {
                return (
                  <Table.Row key={i} className="sections-table-row">
                    <Table.Cell textAlign="center">
                      <NavLink to={`/classes/${section.sectionId}`}>
                        {section.gradeLevel} - {section.name}
                      </NavLink>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {changeCase.titleCase(detail.type)}
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {detail.type === "attendance" ? (
                        <NavLink
                          to={`/attendances/${props.schoolYearId}/${section.sectionId}?quarter=${props.quarter}`}
                        >
                          {detail.status}
                        </NavLink>
                      ) : detail.type === "remark" ? (
                        <NavLink
                          to={`/remarks/${section.sectionId}?quarter=${props.quarter}`}
                        >
                          {detail.status}
                        </NavLink>
                      ) : (
                        <NavLink
                          to={`/classconducts/${section.sectionId}?quarter=${props.quarter}`}
                        >
                          {detail.status}
                        </NavLink>
                      )}
                    </Table.Cell>
                  </Table.Row>
                );
              } else {
                return null;
              }
            });
            return tableRow;
          }
        );

        sectionTable = (
          <Grid centered columns={3}>
            <Grid.Column>
              {props.perSectionSummary.sections.length > 0 ? (
                <Table celled striped size="small">
                  <Table.Header>{headers}</Table.Header>
                  <Table.Body>{tableBody}</Table.Body>
                </Table>
              ) : (
                <h3>No sections available.</h3>
              )}
            </Grid.Column>
          </Grid>
        );
      }

      body = (
        <div>
          {/* HANDLED SECTION */}
          <h3>HANDLED SECTION</h3>
          {hasSectionData ? sectionTable : <TablePlaceholder />}

          {/* SUBJECTS Section */}
          {hasSectionData ? (
            <div>
              <h3>SUBJECTS</h3>
              <ToDoModal
                title="My Class"
                quarter={quarter}
                isGrades={true}
                value={props.perSectionSummary.summary.toDo.count}
                data={props.perSectionSummary.summary.toDo.data}
                sectionSubjects={props.perSectionSummary.sectionSubjects}
                handleNotify={props.handleNotify}
                openConfirm={props.openConfirm}
                openConfirmation={props.openConfirmation}
                closeConfirmation={props.closeConfirmation}
              />
              <CreatedModal
                title="My Class"
                quarter={quarter}
                isGrades={true}
                value={props.perSectionSummary.summary.created.count}
                data={props.perSectionSummary.summary.created.data}
                sectionSubjects={props.perSectionSummary.sectionSubjects}
              />
              <CheckedModal
                title="My Class"
                quarter={quarter}
                isGrades={true}
                value={props.perSectionSummary.summary.checked.count}
                data={props.perSectionSummary.summary.checked.data}
                sectionSubjects={props.perSectionSummary.sectionSubjects}
              />
              <ReviewedModal
                title="My Class"
                quarter={quarter}
                isGrades={true}
                value={props.perSectionSummary.summary.reviewed.count}
                data={props.perSectionSummary.summary.reviewed.data}
                sectionSubjects={props.perSectionSummary.sectionSubjects}
              />
              <ApprovedModal
                title="My Class"
                quarter={quarter}
                isGrades={true}
                value={props.perSectionSummary.summary.approved.count}
                data={props.perSectionSummary.summary.approved.data}
                sectionSubjects={props.perSectionSummary.sectionSubjects}
              />
            </div>
          ) : (
            <SectionPlaceholder title="SUBJECTS" />
          )}

          {/* CONDUCTS Section */}
          {hasConductData ? (
            <div>
              <h3>CONDUCTS</h3>
              <ToDoModal
                title="My Class"
                quarter={quarter}
                isGrades={false}
                value={props.perSectionSummaryConducts.summary.toDo.count}
                data={props.perSectionSummaryConducts.summary.toDo.data}
                sectionSubjects={
                  props.perSectionSummaryConducts.sectionSubjects
                }
                handleNotify={props.handleNotify}
                openConfirm={props.openConfirm}
                openConfirmation={props.openConfirmation}
                closeConfirmation={props.closeConfirmation}
              />
              <CreatedModal
                title="My Class"
                quarter={quarter}
                isGrades={false}
                value={props.perSectionSummaryConducts.summary.created.count}
                data={props.perSectionSummaryConducts.summary.created.data}
                sectionSubjects={
                  props.perSectionSummaryConducts.sectionSubjects
                }
              />
              <CheckedModal
                title="My Class"
                quarter={quarter}
                isGrades={false}
                value={props.perSectionSummaryConducts.summary.checked.count}
                data={props.perSectionSummaryConducts.summary.checked.data}
                sectionSubjects={
                  props.perSectionSummaryConducts.sectionSubjects
                }
              />
              <ReviewedModal
                title="My Class"
                quarter={quarter}
                isGrades={false}
                value={props.perSectionSummaryConducts.summary.reviewed.count}
                data={props.perSectionSummaryConducts.summary.reviewed.data}
                sectionSubjects={
                  props.perSectionSummaryConducts.sectionSubjects
                }
              />
              <ApprovedModal
                title="My Class"
                quarter={quarter}
                isGrades={false}
                value={props.perSectionSummaryConducts.summary.approved.count}
                data={props.perSectionSummaryConducts.summary.approved.data}
                sectionSubjects={
                  props.perSectionSummaryConducts.sectionSubjects
                }
              />
            </div>
          ) : (
            <SectionPlaceholder title="CONDUCTS" />
          )}
        </div>
      );
    }

    // LEVEL VIEW
    else if (props.activeButton === "level") {
      const hasLevelData = !_.isEmpty(props.perLevelSummary);
      const hasLevelConductData = !_.isEmpty(props.perLevelSummaryConducts);

      let level;
      if (props.levelType === "SHS") {
        level = "Senior High School Level";
      } else if (props.levelType === "JHS") {
        level = "Junior High School Level";
      } else if (props.levelType === "ELEMP") {
        level = "Elementary - Primary Level";
      } else if (props.levelType === "ELEMI") {
        level = "Elementary - Intermediate Level";
      } else if (props.levelType === "HS") {
        level = "High School Level";
      } else if (props.levelType === "ELEM") {
        level = "Elementary Level";
      } else {
        level =
          "You are not a level head. Notify the administrator if you are seeing this without proper permission.";
      }

      const headers = (
        <Table.Row>
          <Table.HeaderCell textAlign="center">Section</Table.HeaderCell>
          <Table.HeaderCell textAlign="center">Details Type</Table.HeaderCell>
          <Table.HeaderCell textAlign="center">Status</Table.HeaderCell>
        </Table.Row>
      );

      let levelTable = null;
      if (details && details.length > 0 && !loadingLevel) {
        let currentData = details.filter(props.isSearched(searchKey));
        const indexOfLast = activePage * itemPerPage;
        const indexOfFirst = indexOfLast - itemPerPage;
        const rowLength = currentData.length;
        currentData = currentData.slice(indexOfFirst, indexOfLast);
        const totalPages = Math.ceil(rowLength / itemPerPage);

        const tableBody = currentData.map((detail, key) => {
          return (
            <Table.Row key={key} className="sections-table-row">
              <Table.Cell textAlign="center">
                <NavLink to={`/classes/${detail.section.sectionId}`}>
                  {detail.section.gradeLevel} - {detail.section.name}
                </NavLink>
              </Table.Cell>
              <Table.Cell textAlign="center">
                {changeCase.titleCase(detail.type)}
              </Table.Cell>
              {detail.status === "To Do" ? (
                <Table.Cell textAlign="center">
                  {detail.type === "attendance" ? (
                    <Label
                      as="a"
                      content="Notify"
                      icon="bell"
                      color="red"
                      onClick={() => {
                        props.openConfirmation(
                          `/attendances/${props.schoolYearId}/${detail.section.sectionId}?quarter=${props.quarter}`,
                          detail.section.teacherId
                        );
                      }}
                    />
                  ) : detail.type === "remark" ? (
                    <Label
                      as="a"
                      content="Notify"
                      icon="bell"
                      color="red"
                      onClick={() => {
                        props.openConfirmation(
                          `/remarks/${props.schoolYearId}/${detail.section.sectionId}?quarter=${props.quarter}`,
                          detail.section.teacherId
                        );
                      }}
                    />
                  ) : (
                    <Label
                      as="a"
                      content="Notify"
                      icon="bell"
                      color="red"
                      onClick={() => {
                        props.openConfirmation(
                          `/classconducts/${detail.section.sectionId}?quarter=${props.quarter}`,
                          detail.section.teacherId
                        );
                      }}
                    />
                  )}
                </Table.Cell>
              ) : (
                <Table.Cell textAlign="center">
                  {detail.type === "attendance" ? (
                    <NavLink
                      to={`/attendances/${props.schoolYearId}/${detail.section.sectionId}?quarter=${props.quarter}`}
                    >
                      {detail.status}
                    </NavLink>
                  ) : detail.type === "remark" ? (
                    <NavLink
                      to={`/remarks/${detail.section.sectionId}?quarter=${props.quarter}`}
                    >
                      {detail.status}
                    </NavLink>
                  ) : (
                    <NavLink
                      to={`/classconducts/${detail.section.sectionId}?quarter=${props.quarter}`}
                    >
                      {detail.status}
                    </NavLink>
                  )}
                </Table.Cell>
              )}
            </Table.Row>
          );
        });

        levelTable = (
          <Grid centered columns={2}>
            <Grid.Column>
              <Input
                fluid
                icon="search"
                value={searchKey}
                onChange={props.onSearchChange}
                placeholder="Search Sections"
              />
              <Table celled striped size="small" compact>
                <Table.Header>{headers}</Table.Header>
                <Table.Body>{tableBody}</Table.Body>
                {details.length > itemPerPage && (
                  <PaginationTable
                    onPageChange={props.handlePaginationChange}
                    totalPages={totalPages}
                    activePage={activePage}
                    fields={["Section", "Type", "Status"]}
                  />
                )}
              </Table>
            </Grid.Column>
          </Grid>
        );
      }

      body = (
        <div>
          <h3>{level}</h3>

          {/* HANDLED SECTIONS */}
          <h3>HANDLED SECTIONS</h3>
          {loadingLevel ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Loader active inline="centered" />
              <p>Loading sections...</p>
            </div>
          ) : levelTable ? (
            levelTable
          ) : (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <p>No sections available.</p>
            </div>
          )}

          {/* SUBJECTS Section */}
          <h3>SUBJECTS</h3>
          <LevelSummaryLoadingState
            loadingLevelSummary={props.loadingLevelSummary}
            levelSummaryError={props.levelSummaryError}
            levelSummaryRetryCount={props.levelSummaryRetryCount}
            retryLevelSummary={props.retryLevelSummary}
            loadingData={props.loadingData}
            hasData={hasLevelData}
          />
          
          {hasLevelData ? (
            <div>
              <ToDoModal
                title="Level Summary"
                quarter={quarter}
                isGrades={true}
                value={props.perLevelSummary.summary.toDo.count}
                data={props.perLevelSummary.summary.toDo.data}
                sectionSubjects={props.perLevelSummary.sectionSubjects}
                handleNotify={props.handleNotify}
                openConfirm={props.openConfirm}
                openConfirmation={props.openConfirmation}
                closeConfirmation={props.closeConfirmation}
              />
              <CreatedModal
                title="Level Summary"
                quarter={quarter}
                isGrades={true}
                value={props.perLevelSummary.summary.created.count}
                data={props.perLevelSummary.summary.created.data}
                sectionSubjects={props.perLevelSummary.sectionSubjects}
              />
              <CheckedModal
                title="Level Summary"
                quarter={quarter}
                isGrades={true}
                value={props.perLevelSummary.summary.checked.count}
                data={props.perLevelSummary.summary.checked.data}
                sectionSubjects={props.perLevelSummary.sectionSubjects}
              />
              <ReviewedModal
                title="Level Summary"
                quarter={quarter}
                isGrades={true}
                value={props.perLevelSummary.summary.reviewed.count}
                data={props.perLevelSummary.summary.reviewed.data}
                sectionSubjects={props.perLevelSummary.sectionSubjects}
              />
              <ApprovedModal
                title="Level Summary"
                quarter={quarter}
                isGrades={true}
                value={props.perLevelSummary.summary.approved.count}
                data={props.perLevelSummary.summary.approved.data}
                sectionSubjects={props.perLevelSummary.sectionSubjects}
              />
            </div>
          ) : (!props.loadingLevelSummary && !props.levelSummaryError) ? (
            <SectionPlaceholder title="" />
          ) : null}

          {/* CONDUCTS Section */}
          {hasLevelConductData ? (
            <div>
              <h3>CONDUCTS</h3>
              <ToDoModal
                title="Level Summary"
                quarter={quarter}
                isGrades={false}
                value={props.perLevelSummaryConducts.summary.toDo.count}
                data={props.perLevelSummaryConducts.summary.toDo.data}
                sectionSubjects={props.perLevelSummaryConducts.sectionSubjects}
                handleNotify={props.handleNotify}
                openConfirm={props.openConfirm}
                openConfirmation={props.openConfirmation}
                closeConfirmation={props.closeConfirmation}
              />
              <CreatedModal
                title="Level Summary"
                quarter={quarter}
                isGrades={false}
                value={props.perLevelSummaryConducts.summary.created.count}
                data={props.perLevelSummaryConducts.summary.created.data}
                sectionSubjects={props.perLevelSummaryConducts.sectionSubjects}
              />
              <CheckedModal
                title="Level Summary"
                quarter={quarter}
                isGrades={false}
                value={props.perLevelSummaryConducts.summary.checked.count}
                data={props.perLevelSummaryConducts.summary.checked.data}
                sectionSubjects={props.perLevelSummaryConducts.sectionSubjects}
              />
              <ReviewedModal
                title="Level Summary"
                quarter={quarter}
                isGrades={false}
                value={props.perLevelSummaryConducts.summary.reviewed.count}
                data={props.perLevelSummaryConducts.summary.reviewed.data}
                sectionSubjects={props.perLevelSummaryConducts.sectionSubjects}
              />
              <ApprovedModal
                title="Level Summary"
                quarter={quarter}
                isGrades={false}
                value={props.perLevelSummaryConducts.summary.approved.count}
                data={props.perLevelSummaryConducts.summary.approved.data}
                sectionSubjects={props.perLevelSummaryConducts.sectionSubjects}
              />
            </div>
          ) : (
            <SectionPlaceholder title="CONDUCTS" />
          )}
        </div>
      );
    }

    // ADMIN/ALL VIEW
    else if (props.activeButton === "all") {
      const hasQuarterData = !_.isEmpty(props.perQuarterSummary);
      const hasQuarterConductData = !_.isEmpty(props.perQuarterSummaryConducts);

      const quarterLabel = _.find(optionsQuarters, { value: props.quarter });

      const headers = (
        <Table.Row>
          <Table.HeaderCell textAlign="center">Section</Table.HeaderCell>
          <Table.HeaderCell textAlign="center">Details Type</Table.HeaderCell>
          <Table.HeaderCell textAlign="center">Status</Table.HeaderCell>
        </Table.Row>
      );

      let adminTable = null;
      if (detailsAdmin && detailsAdmin.length > 0 && !loadingAdmin) {
        let currentData = detailsAdmin.filter(props.isSearched(searchKey));
        const indexOfLast = activePage * itemPerPage;
        const indexOfFirst = indexOfLast - itemPerPage;
        const rowLength = currentData.length;
        currentData = currentData.slice(indexOfFirst, indexOfLast);
        const totalPages = Math.ceil(rowLength / itemPerPage);

        const tableBody = currentData.map((detail, key) => {
          return (
            <Table.Row key={key} className="sections-table-row">
              <Table.Cell textAlign="center">
                <NavLink to={`/classes/${detail.section.sectionId}`}>
                  {detail.section.gradeLevel} - {detail.section.name}
                </NavLink>
              </Table.Cell>
              <Table.Cell textAlign="center">
                {changeCase.titleCase(detail.type)}
              </Table.Cell>
              {detail.status === "To Do" ? (
                <Table.Cell textAlign="center">
                  {detail.type === "attendance" ? (
                    <Label
                      as="a"
                      content="Notify"
                      icon="bell"
                      color="red"
                      onClick={() => {
                        props.openConfirmation(
                          `/attendances/${props.schoolYearId}/${detail.section.sectionId}?quarter=${props.quarter}`,
                          detail.section.teacherId
                        );
                      }}
                    />
                  ) : detail.type === "remark" ? (
                    <Label
                      as="a"
                      content="Notify"
                      icon="bell"
                      color="red"
                      onClick={() => {
                        props.openConfirmation(
                          `/remarks/${detail.section.sectionId}?quarter=${props.quarter}`,
                          detail.section.teacherId
                        );
                      }}
                    />
                  ) : (
                    <Label
                      as="a"
                      content="Notify"
                      icon="bell"
                      color="red"
                      onClick={() => {
                        props.openConfirmation(
                          `/classconducts/${detail.section.sectionId}?quarter=${props.quarter}`,
                          detail.section.teacherId
                        );
                      }}
                    />
                  )}
                </Table.Cell>
              ) : (
                <Table.Cell textAlign="center">
                  {detail.type === "attendance" ? (
                    <NavLink
                      to={`/attendances/${props.schoolYearId}/${detail.section.sectionId}?quarter=${props.quarter}`}
                    >
                      {detail.status}
                    </NavLink>
                  ) : detail.type === "remark" ? (
                    <NavLink
                      to={`/remarks/${detail.section.sectionId}?quarter=${props.quarter}`}
                    >
                      {detail.status}
                    </NavLink>
                  ) : (
                    <NavLink
                      to={`/classconducts/${detail.section.sectionId}?quarter=${props.quarter}`}
                    >
                      {detail.status}
                    </NavLink>
                  )}
                </Table.Cell>
              )}
            </Table.Row>
          );
        });

        adminTable = (
          <Grid centered columns={2}>
            <Grid.Column>
              <Input
                fluid
                icon="search"
                value={searchKey}
                onChange={props.onSearchChange}
                placeholder="Search Sections"
              />
              <Table celled striped size="small" compact>
                <Table.Header>{headers}</Table.Header>
                <Table.Body>{tableBody}</Table.Body>
                {detailsAdmin.length > itemPerPage && (
                  <PaginationTable
                    onPageChange={props.handlePaginationChange}
                    totalPages={totalPages}
                    activePage={activePage}
                    fields={["Section", "Type", "Status"]}
                  />
                )}
              </Table>
            </Grid.Column>
          </Grid>
        );
      }

      body = (
        <div>
          <h3>{quarterLabel && quarterLabel.text}</h3>

          {/* HANDLED SECTIONS */}
          <h3>HANDLED SECTIONS</h3>
          {loadingAdmin ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Loader active inline="centered" />
              <p>Loading admin sections...</p>
            </div>
          ) : adminTable ? (
            adminTable
          ) : (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <p>No sections available.</p>
            </div>
          )}

          {/* SUBJECTS Section */}
          <h3>SUBJECTS</h3>
          <AdminGradesLoadingState
            loadingQuarterSummary={props.loadingQuarterSummary}
            quarterSummaryError={props.quarterSummaryError}
            quarterSummaryRetryCount={props.quarterSummaryRetryCount}
            retryQuarterSummary={props.retryQuarterSummary}
            loadingData={props.loadingData}
            hasData={hasQuarterData}
          />
          
          {hasQuarterData ? (
            <div>
              <ToDoModal
                title="Quarter Summary"
                quarter={quarter}
                isGrades={true}
                value={props.perQuarterSummary.summary.toDo.count}
                data={props.perQuarterSummary.summary.toDo.data}
                sectionSubjects={props.perQuarterSummary.sectionSubjects}
                handleNotify={props.handleNotify}
                openConfirm={props.openConfirm}
                openConfirmation={props.openConfirmation}
                closeConfirmation={props.closeConfirmation}
              />
              <CreatedModal
                title="Quarter Summary"
                quarter={quarter}
                isGrades={true}
                value={props.perQuarterSummary.summary.created.count}
                data={props.perQuarterSummary.summary.created.data}
                sectionSubjects={props.perQuarterSummary.sectionSubjects}
              />
              <CheckedModal
                title="Quarter Summary"
                quarter={quarter}
                isGrades={true}
                value={props.perQuarterSummary.summary.checked.count}
                data={props.perQuarterSummary.summary.checked.data}
                sectionSubjects={props.perQuarterSummary.sectionSubjects}
              />
              <ReviewedModal
                title="Quarter Summary"
                quarter={quarter}
                isGrades={true}
                value={props.perQuarterSummary.summary.reviewed.count}
                data={props.perQuarterSummary.summary.reviewed.data}
                sectionSubjects={props.perQuarterSummary.sectionSubjects}
              />
              <ApprovedModal
                title="Quarter Summary"
                quarter={quarter}
                isGrades={true}
                value={props.perQuarterSummary.summary.approved.count}
                data={props.perQuarterSummary.summary.approved.data}
                sectionSubjects={props.perQuarterSummary.sectionSubjects}
              />
            </div>
          ) : (!props.loadingQuarterSummary && !props.quarterSummaryError) ? (
            <SectionPlaceholder title="" />
          ) : null}

          {/* CONDUCTS Section */}
          <h3>CONDUCTS</h3>
          <AdminConductsLoadingState
            loadingQuarterSummaryConducts={props.loadingQuarterSummaryConducts}
            quarterSummaryConductsError={props.quarterSummaryConductsError}
            quarterSummaryConductsRetryCount={props.quarterSummaryConductsRetryCount}
            retryQuarterSummaryConducts={props.retryQuarterSummaryConducts}
            loadingData={props.loadingData}
            hasData={hasQuarterConductData}
          />
          
          {hasQuarterConductData ? (
            <div>
              <ToDoModal
                title="Quarter Summary"
                quarter={quarter}
                isGrades={false}
                value={props.perQuarterSummaryConducts.summary.toDo.count}
                data={props.perQuarterSummaryConducts.summary.toDo.data}
                sectionSubjects={props.perQuarterSummary.sectionSubjects}
                handleNotify={props.handleNotify}
                openConfirm={props.openConfirm}
                openConfirmation={props.openConfirmation}
                closeConfirmation={props.closeConfirmation}
              />
              <CreatedModal
                title="Quarter Summary"
                quarter={quarter}
                isGrades={false}
                value={props.perQuarterSummaryConducts.summary.created.count}
                data={props.perQuarterSummaryConducts.summary.created.data}
                sectionSubjects={
                  props.perQuarterSummaryConducts.sectionSubjects
                }
              />
              <CheckedModal
                title="Quarter Summary"
                quarter={quarter}
                isGrades={false}
                value={props.perQuarterSummaryConducts.summary.checked.count}
                data={props.perQuarterSummaryConducts.summary.checked.data}
                sectionSubjects={
                  props.perQuarterSummaryConducts.sectionSubjects
                }
              />
              <ReviewedModal
                title="Quarter Summary"
                quarter={quarter}
                isGrades={false}
                value={props.perQuarterSummaryConducts.summary.reviewed.count}
                data={props.perQuarterSummaryConducts.summary.reviewed.data}
                sectionSubjects={
                  props.perQuarterSummaryConducts.sectionSubjects
                }
              />
              <ApprovedModal
                title="Quarter Summary"
                quarter={quarter}
                isGrades={false}
                value={props.perQuarterSummaryConducts.summary.approved.count}
                data={props.perQuarterSummaryConducts.summary.approved.data}
                sectionSubjects={
                  props.perQuarterSummaryConducts.sectionSubjects
                }
              />
            </div>
          ) : (!props.loadingQuarterSummaryConducts && !props.quarterSummaryConductsError) ? (
            <SectionPlaceholder title="" />
          ) : null}
        </div>
      );
    }

    // INITIAL LOADING STATE
    else {
      body = (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <Loader active inline="centered" />
          <p>Loading dashboard...</p>
        </div>
      );
    }

    // Always show the main interface, never block it completely
    return (
      <div>
        <Segment textAlign="center">
          <h3>Grading System Dashboard</h3>

          {/* Show dropdowns even while loading */}
          <div className="school-year-dropdown">
            <Dropdown
              selection
              name="schoolYearId"
              options={optionsSchoolYears || []}
              onChange={props.handleChangeSchoolYear}
              value={props.schoolYearId}
              loading={!optionsSchoolYears}
              disabled={loadingData}
            />
            <Dropdown
              selection
              name="quarter"
              options={optionsQuarters}
              onChange={props.handleChangeQuarter}
              value={props.quarter}
              disabled={loadingData}
            />
          </div>

          {/* Show navigation buttons */}
          <div>
            <Button
              inverted
              color="blue"
              active={props.activeButton === "subject"}
              onClick={() => props.handleClick("subject")}
              loading={props.activeButton === "subject" && loadingData}
            >
              My Subjects
            </Button>

            {(props.userType === "admin" || props.userType === "adviser") && (
              <Button
                inverted
                color="blue"
                active={props.activeButton === "section"}
                onClick={() => props.handleClick("section")}
                loading={props.activeButton === "section" && loadingData}
              >
                My Class
              </Button>
            )}

            {(props.userType === "admin" || props.userType === "levelHead") && (
              <Button
                inverted
                color="blue"
                active={props.activeButton === "level"}
                onClick={() => props.handleClick("level")}
                loading={props.activeButton === "level" && loadingData}
              >
                Level Summary
              </Button>
            )}

            {(props.userType === "admin" ||
              props.userType === "principal" ||
              props.userType === "registrar") && (
              <Button
                inverted
                color="blue"
                active={props.activeButton === "all"}
                onClick={() => props.handleClick("all")}
                loading={props.activeButton === "all" && loadingData}
              >
                Quarter Summary
              </Button>
            )}

            <Confirm
              size="tiny"
              open={props.openConfirm}
              onCancel={props.closeConfirmation}
              onConfirm={props.handleNotify}
            />
          </div>

          <Divider />

          {/* Show body content with progressive loading */}
          {body}

          {/* CLUBS reads as a third section after SUBJECTS and CONDUCTS, which
              is where someone looks for it - but it sits outside the per-role
              views rather than inside them. Which summary a role is shown has
              nothing to do with clubs: an adviser moderates one as readily as a
              subject teacher, and a level head or principal has a rung on the
              ladder whatever view they get. Inside the subject view it was
              invisible to everyone but teachers. Absent entirely for anyone
              with no part in any club. */}
          {props.myClubs && props.myClubs.length > 0 && (
            <div>
              <Divider />
              <h3>CLUBS</h3>
              <ClubsModal clubs={props.myClubs} quarter={quarter} />
            </div>
          )}
        </Segment>
      </div>
    );
  } else {
    return <Redirect to="/" />;
  }
};

export default HomePV;
